import { consentService, type ConsentDecision, onConsentChange } from '../../services/consent';
import { onLocaleChange, t } from '../../services/i18n';

export class RcConsentBanner extends HTMLElement {
  private disposeLocaleChange: (() => void) | null = null;
  private disposeConsentChange: (() => void) | null = null;

  connectedCallback(): void {
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    this.disposeConsentChange = onConsentChange(() => this.render());
    this.render();
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    this.disposeConsentChange?.();
    this.disposeLocaleChange = null;
    this.disposeConsentChange = null;
  }

  private render(): void {
    const decision = consentService.getDecision();
    const hidden = decision !== null;

    this.innerHTML = `
      <div class="consent-banner${hidden ? ' is-hidden' : ''}" data-consent-banner ${hidden ? 'hidden' : ''} role="dialog" aria-modal="false" aria-labelledby="consent-banner-title">
        <div class="consent-banner-card">
          <div class="consent-banner-copy">
            <p class="consent-banner-eyebrow">${this.escape(t('consent.eyebrow'))}</p>
            <h2 id="consent-banner-title">${this.escape(t('consent.title'))}</h2>
            <p>${this.escape(t('consent.description'))}</p>
            <p class="consent-banner-note">${this.escape(this.getDecisionLabel(decision))}</p>
          </div>
          <div class="consent-banner-actions">
            <button type="button" class="btn btn-outline" data-consent-choice="necessary">${this.escape(t('consent.actions.necessary'))}</button>
            <button type="button" class="btn btn-primary" data-consent-choice="all">${this.escape(t('consent.actions.all'))}</button>
          </div>
        </div>
      </div>
    `;

    if (hidden) {
      return;
    }

    this.querySelectorAll<HTMLButtonElement>('[data-consent-choice]').forEach((button) => {
      button.addEventListener('click', () => {
        const choice = button.dataset.consentChoice as ConsentDecision | undefined;
        if (!choice) {
          return;
        }

        consentService.setDecision(choice);
      });
    });
  }

  private getDecisionLabel(decision: ConsentDecision | null): string {
    if (decision === 'necessary') {
      return t('consent.saved.necessary');
    }

    if (decision === 'all') {
      return t('consent.saved.all');
    }

    return t('consent.saved.pending');
  }

  private escape(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('rc-consent-banner')) {
  customElements.define('rc-consent-banner', RcConsentBanner);
}
